import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { partnersApi } from "../api/client";

const PlaceholderCard = () => (
  <div className="animate-pulse p-4 bg-white/60 dark:bg-gray-900/60 rounded-2xl backdrop-blur-sm border border-white/10">
    <div className="h-20 w-full bg-gray-100 dark:bg-gray-700 rounded mb-3" />
    <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded w-3/4 mb-2" />
    <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded w-5/6" />
  </div>
);

const LogoItem = ({ src, title }) => (
  <div className="flex items-center justify-center h-20 w-36 p-2">
    {src ? (
      // preserve aspect ratio and center
      <img
        src={src}
        alt={title || "partner"}
        className="max-h-full max-w-full object-contain"
      />
    ) : (
      <div className="h-12 w-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-600">
        {title ? title.slice(0, 1).toUpperCase() : "P"}
      </div>
    )}
  </div>
);

const PartnersSection = ({ limit = 12 }) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    // Respect OS-level reduced motion preference
    const mq =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = () => setReducedMotion(!!(mq && mq.matches));
    handler();
    if (mq && mq.addEventListener) mq.addEventListener("change", handler);
    else if (mq && mq.addListener) mq.addListener(handler);
    return () => {
      if (mq && mq.removeEventListener)
        mq.removeEventListener("change", handler);
      else if (mq && mq.removeListener) mq.removeListener(handler);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const res = await partnersApi.list({ limit });
        if (!mounted) return;
        const payload = res.data;
        setPartners(Array.isArray(payload) ? payload : payload?.partners || []);
      } catch (e) {
        console.error("Failed to load partners", e);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => (mounted = false);
  }, [limit]);

  const logos = useMemo(
    () => partners.map((p) => ({ src: p.imageUrl, title: p.title })),
    [partners]
  );

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
        {Array.from({ length: Math.min(8, limit) }).map((_, i) => (
          <PlaceholderCard key={i} />
        ))}
      </div>
    );
  }

  if (!partners.length)
    return (
      <div>
        <h3 className="text-center text-3xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-indigo-600">
          Trusted by top partners
        </h3>
        <p className="py-8 text-center text-gray-600 dark:text-gray-400">
          {" "}
          No partners yet.
        </p>
      </div>
    );

  return (
    <section className="py-10">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-8">
          <h3 className="text-3xl sm:text-4xl font-extrabold text-primary">
            Trusted by top partners
          </h3>
          <p className="mt-2 text-gray-800 font-medium dark:text-gray-300 max-w-2xl text-lg mx-auto">
            We partner with innovative companies to bring seamless payment
            experiences to millions. Here's a selection of partners we work
            with.
          </p>
        </div>

        {/* Logo marquee */}
        <div className="overflow-hidden relative rounded-3xl border border-white/10 bg-gradient-to-r from-white/5 via-white/3 to-white/5 p-4 mb-8">
          <motion.div
            className="flex items-center space-x-6"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            animate={
              reducedMotion || paused ? { x: "0%" } : { x: ["0%", "-50%"] }
            }
            transition={
              reducedMotion || paused
                ? undefined
                : {
                    repeat: Infinity,
                    repeatType: "loop",
                    duration: Math.max(12, logos.length * 1.2),
                    ease: "linear",
                  }
            }
          >
            {/* duplicate list for seamless loop */}
            {[...logos, ...logos].map((l, i) => (
              <div key={i} className="flex-shrink-0">
                <LogoItem src={l.src} title={l.title} />
              </div>
            ))}
          </motion.div>
        </div>

        {/* Grid of glass cards
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {partners.map((p) => (
            <motion.div
              key={p._id}
              whileHover={{ y: -8, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300, damping: 18 }}
              className="p-6 rounded-2xl bg-white/40 dark:bg-gray-900/40 backdrop-blur-md border border-white/10 hover:shadow-xl"
            >
              <div className="flex items-center space-x-4">
                <div className="h-16 w-16 flex items-center justify-center rounded-lg bg-white/60 dark:bg-gray-800/60 overflow-hidden">
                  {p.imageUrl ? (
                    <img
                      src={p.imageUrl}
                      alt={p.title || "partner"}
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-600">
                      {p.title ? p.title.slice(0, 1).toUpperCase() : "P"}
                    </div>
                  )}
                </div>

                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    {p.title}
                  </div>
                  {p.text && (
                    <div className="text-xs text-gray-600 dark:text-gray-300 mt-1 line-clamp-3">
                      {p.text}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Partner since {p.since || "—"}
                </div>
                <button
                  onClick={() => window.open(p.website || "#", "_blank")}
                  className="text-xs px-3 py-1 rounded-full bg-gradient-to-r from-indigo-600 to-fuchsia-500 text-white shadow-sm"
                >
                  Visit
                </button>
              </div>
            </motion.div>
          ))}
        </div> */}

        {/* <div className="mt-8 text-center">
          <button
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-rose-500 to-orange-400 text-white font-semibold shadow-lg"
            onClick={() => window.location.assign("/partners")}
          >
            See all partners
          </button>
        </div> */}
      </div>
    </section>
  );
};

export default PartnersSection;
